// src/events/index.ts
var EventCriticality = {
  CRITICAL: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
  SUCCESS: 5
};
var EVENT_CRITICALITY_ORDER = [
  EventCriticality.CRITICAL,
  EventCriticality.HIGH,
  EventCriticality.MEDIUM,
  EventCriticality.LOW,
  EventCriticality.SUCCESS
];
var EVENT_CRITICALITY_LABELS = {
  [EventCriticality.CRITICAL]: "Critical",
  [EventCriticality.HIGH]: "High",
  [EventCriticality.MEDIUM]: "Medium",
  [EventCriticality.LOW]: "Low",
  [EventCriticality.SUCCESS]: "Success"
};
function criticalityLabel(level) {
  return EVENT_CRITICALITY_LABELS[level] ?? `Level ${level}`;
}
var CRITICALITY_BY_NAME = {
  critical: EventCriticality.CRITICAL,
  fatal: EventCriticality.CRITICAL,
  emergency: EventCriticality.CRITICAL,
  high: EventCriticality.HIGH,
  error: EventCriticality.HIGH,
  warn: EventCriticality.MEDIUM,
  warning: EventCriticality.MEDIUM,
  medium: EventCriticality.MEDIUM,
  low: EventCriticality.LOW,
  info: EventCriticality.LOW,
  notice: EventCriticality.LOW,
  success: EventCriticality.SUCCESS,
  ok: EventCriticality.SUCCESS,
  resolved: EventCriticality.SUCCESS
};
function criticalityFromName(value, fallback = EventCriticality.MEDIUM) {
  if (value == null) return fallback;
  if (typeof value === "number") {
    return EVENT_CRITICALITY_ORDER.includes(value) ? value : fallback;
  }
  const trimmed = value.trim();
  if (trimmed !== "" && Number.isFinite(Number(trimmed))) {
    const n = Number(trimmed);
    if (EVENT_CRITICALITY_ORDER.includes(n)) {
      return n;
    }
  }
  return CRITICALITY_BY_NAME[trimmed.toLowerCase()] ?? fallback;
}
function normalizeEvent(event) {
  const level = criticalityFromName(event.criticality_level);
  if (event.criticality_level === level) return event;
  return { ...event, criticality_level: level };
}
var EventResourceType = {
  ADDRESS: "address",
  TRANSACTION: "transaction",
  LABEL: "label",
  TAG: "tag",
  GROUP: "group",
  ENTITY: "entity",
  ABI: "abi",
  PROXY: "proxy",
  STREAM: "stream",
  USER: "user",
  ORGANIZATION: "organization",
  TEAM: "team",
  INVESTIGATION: "investigation",
  PIPELINE: "pipeline",
  TRACER: "tracer",
  /** Alerting rules, watches and their delivery destinations. */
  TRIGGER: "trigger",
  /** Settings that change how data is INTERPRETED: decoding overrides,
   *  confidence policy, evidence policy, grouping rules. */
  CONFIGURATION: "configuration",
  /** Inbound contact / lead events from `aws-connect-mgmt`. */
  APPLICATION: "application",
  AUTHENTICATION: "authentication",
  ACTIVITY_LOG: "activity_log"
};
var EVENT_RESOURCE_LABELS = {
  [EventResourceType.ADDRESS]: "Address",
  [EventResourceType.TRANSACTION]: "Transaction",
  [EventResourceType.LABEL]: "Label",
  [EventResourceType.TAG]: "Tag",
  [EventResourceType.GROUP]: "Group",
  [EventResourceType.ENTITY]: "Entity",
  [EventResourceType.ABI]: "ABI",
  [EventResourceType.PROXY]: "Proxy",
  [EventResourceType.STREAM]: "Stream",
  [EventResourceType.USER]: "User",
  [EventResourceType.ORGANIZATION]: "Organization",
  [EventResourceType.TEAM]: "Team",
  [EventResourceType.INVESTIGATION]: "Investigation",
  [EventResourceType.PIPELINE]: "Pipeline",
  [EventResourceType.TRACER]: "Tracer",
  [EventResourceType.TRIGGER]: "Trigger",
  [EventResourceType.CONFIGURATION]: "Configuration",
  [EventResourceType.APPLICATION]: "Application",
  [EventResourceType.AUTHENTICATION]: "Authentication",
  [EventResourceType.ACTIVITY_LOG]: "Activity log"
};
var EVENT_RESOURCE_ORDER = [
  EventResourceType.ADDRESS,
  EventResourceType.TRANSACTION,
  EventResourceType.ABI,
  EventResourceType.LABEL,
  EventResourceType.TAG,
  EventResourceType.ENTITY,
  EventResourceType.GROUP,
  EventResourceType.PROXY,
  EventResourceType.INVESTIGATION,
  EventResourceType.PIPELINE,
  EventResourceType.TRACER,
  EventResourceType.TRIGGER,
  EventResourceType.CONFIGURATION,
  EventResourceType.STREAM,
  EventResourceType.USER,
  EventResourceType.TEAM,
  EventResourceType.ORGANIZATION,
  EventResourceType.AUTHENTICATION,
  EventResourceType.APPLICATION,
  EventResourceType.ACTIVITY_LOG
];
function resourceLabel(resource) {
  return EVENT_RESOURCE_LABELS[resource] ?? resource;
}
var EventChainType = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  OTHER: "other"
};
function eventActorId(event) {
  if (typeof event.created_by === "string" && event.created_by) return event.created_by;
  const meta = event.metadata ?? {};
  const candidate = meta["actor_user_id"] ?? meta["audience_user_id"];
  return typeof candidate === "string" && candidate ? candidate : null;
}
function formatPrincipal(id) {
  if (!id) return "Someone";
  const tail = id.includes("|") ? id.slice(id.lastIndexOf("|") + 1) : id;
  if (/^\d{8,}$/.test(tail)) return `User \u2026${tail.slice(-4)}`;
  return tail;
}
function eventScopeLabel(event, ctx) {
  const orgId = event.org_id ?? event.org ?? null;
  const tenantId = event.tenant_id ?? null;
  const org = orgId ? ctx.resolveOrgLabel?.(orgId) ?? orgId : null;
  const tenant = tenantId ? ctx.resolveTenantLabel?.(tenantId) ?? tenantId : null;
  const parts = [org, tenant].filter((p) => Boolean(p) && p !== "unknown");
  return parts.length ? parts.join(" \xB7 ") : null;
}
function isAuthentication(event) {
  return event.resource === EventResourceType.AUTHENTICATION;
}
function isGroup(event) {
  return event.resource === EventResourceType.GROUP;
}
function groupEventTargetId(event) {
  if (!isGroup(event)) return null;
  const id = event.group_id || event.link_id;
  return typeof id === "string" && id ? id : null;
}
function describeEvent(event, ctx = {}) {
  const actorId = eventActorId(event);
  const isCurrentUser = Boolean(ctx.currentUserId && actorId === ctx.currentUserId);
  const resolvedName = actorId ? ctx.resolveActorName?.(actorId) ?? null : null;
  const actorDisplay = isCurrentUser ? "You" : resolvedName ?? formatPrincipal(actorId);
  const scope = eventScopeLabel(event, ctx);
  if (isAuthentication(event)) {
    const kind = (event.authentication_type ?? "").toLowerCase();
    const denied = event.metadata?.["login_allowed"] === false;
    let title2;
    if (denied) {
      title2 = isCurrentUser ? "Your sign-in was denied" : `${actorDisplay} was denied sign-in`;
    } else if (kind === "logout") {
      title2 = isCurrentUser ? "You signed out" : `${actorDisplay} signed out`;
    } else if (isCurrentUser && resolvedName) {
      title2 = `Signed in as ${resolvedName}`;
    } else if (isCurrentUser) {
      title2 = "You signed in";
    } else {
      title2 = `${actorDisplay} signed in`;
    }
    return {
      title: title2,
      description: scope ? `Workspace ${scope}` : void 0,
      actorId,
      isCurrentUser
    };
  }
  if (isGroup(event)) {
    const kind = (event.lifecycle_kind ?? "").toLowerCase();
    const state = (event.state ?? "").toLowerCase();
    let fallback = event.message_summary;
    if (!fallback) {
      if (kind === "group.leg_arrived") {
        fallback = state === "completed" ? "Cross-chain group complete" : "Next leg arrived";
      } else if (kind === "group.timed_out") {
        fallback = "Cross-chain group timed out";
      } else {
        fallback = resourceLabel(event.resource);
      }
    }
    return {
      title: fallback,
      description: event.message || (scope ? `Workspace ${scope}` : void 0),
      actorId,
      isCurrentUser
    };
  }
  const base = event.message_summary || resourceLabel(event.resource);
  const title = isCurrentUser && !/\byou\b/i.test(base) ? `${base} \xB7 by you` : base;
  return {
    title,
    description: event.message || (scope ? `Workspace ${scope}` : void 0),
    actorId,
    isCurrentUser
  };
}

export { CRITICALITY_BY_NAME, EVENT_CRITICALITY_LABELS, EVENT_CRITICALITY_ORDER, EVENT_RESOURCE_LABELS, EVENT_RESOURCE_ORDER, EventChainType, EventCriticality, EventResourceType, criticalityFromName, criticalityLabel, describeEvent, eventActorId, formatPrincipal, groupEventTargetId, normalizeEvent, resourceLabel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map